import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'en', flag: '/argon-assets/img/icons/flags/GB.png', label: 'English' },
  { code: 'fr', flag: '/argon-assets/img/icons/flags/FR.png', label: 'Français' },
  { code: 'it', flag: '/argon-assets/img/icons/flags/IT.png', label: 'Italiano' },
  { code: 'de', flag: '/argon-assets/img/icons/flags/DE.png', label: 'Deutsch' },
  { code: 'es', flag: '/argon-assets/img/icons/flags/ES.png', label: 'Español' },
];

const PublicNavbar: React.FC = () => {
  const location = useLocation();
  const { i18n, t } = useTranslation();

  const [langOpen, setLangOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const langRef = useRef<HTMLLIElement>(null);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const currentLang = LANGUAGES.find(l => l.code === (i18n.language || 'en').substring(0, 2)) || LANGUAGES[0];

  return (
    <>
      <style>{`
        /* Override Bootstrap primary colors to navy blue */
        .btn-primary {
          background-color: var(--primary) !important;
          border-color: var(--primary) !important;
        }
        .btn-primary:hover {
          background-color: #1B5694 !important;
          border-color: #1B5694 !important;
        }
        .btn-outline-primary {
          color: var(--primary) !important;
          border-color: var(--primary) !important;
        }
        .btn-outline-primary:hover {
          background-color: var(--primary) !important;
          border-color: var(--primary) !important;
          color: #fff !important;
        }

        /* ---- Mega-menu dropdown ---- */
        .nav-mega {
          position: relative;
        }
        .nav-mega > .nav-link {
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          text-transform: uppercase;
          font-weight: 600;
          font-size: 0.85rem;
          letter-spacing: 0.04em;
        }
        .nav-mega > .nav-link::after {
          content: '';
          display: inline-block;
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-top: 4px solid currentColor;
          margin-left: 4px;
          transition: transform 0.2s;
        }
        .mega-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          min-width: 240px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          padding: 0.75rem 0;
          opacity: 0;
          visibility: hidden;
          transform: translateY(8px);
          transition: opacity 0.2s, transform 0.2s, visibility 0.2s;
          z-index: 1050;
        }
        .nav-mega:hover > .mega-dropdown,
        .nav-mega:focus-within > .mega-dropdown {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
        .mega-dropdown .mega-item {
          display: block;
          padding: 0.5rem 1.25rem;
          color: #32325d;
          text-decoration: none;
          transition: background 0.15s;
        }
        .mega-dropdown .mega-item:hover {
          background: #f6f9fc;
          color: var(--primary);
          text-decoration: none;
        }
        .mega-dropdown .mega-item-title {
          font-weight: 600;
          font-size: 0.85rem;
          margin-bottom: 0;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .mega-dropdown .mega-item-desc {
          font-size: 0.82rem;
          color: #6b7c93;
          margin: 0;
          text-transform: none;
          letter-spacing: 0.01em;
        }

        /* ---- Language dropdown ---- */
        .lang-selector {
          position: relative;
        }
        .lang-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          background: none;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          padding: 4px 10px;
          cursor: pointer;
          font-size: 0.85rem;
          color: #525f7f;
          transition: border-color 0.2s;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          font-weight: 600;
        }
        .lang-toggle:hover {
          border-color: var(--primary);
        }
        .lang-toggle img {
          width: 20px;
          height: 14px;
          object-fit: cover;
        }
        .lang-toggle-caret {
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-top: 4px solid currentColor;
        }
        .lang-dropdown-menu {
          position: absolute;
          top: calc(100% + 4px);
          right: 0;
          min-width: 160px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          padding: 0.5rem 0;
          z-index: 1060;
          display: none;
        }
        .lang-open > .lang-dropdown-menu {
          display: block;
        }
        .lang-dropdown-menu button {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          border: none;
          background: none;
          padding: 0.4rem 1rem;
          font-size: 0.85rem;
          color: #32325d;
          cursor: pointer;
          transition: background 0.15s;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .lang-dropdown-menu button:hover {
          background: #f6f9fc;
        }
        .lang-dropdown-menu button img {
          width: 20px;
          height: 14px;
          object-fit: cover;
        }
        .lang-dropdown-menu button.active {
          font-weight: 600;
          color: var(--primary);
        }

        /* ---- Mobile menu styling ---- */
        @media (max-width: 991.98px) {
          /* Animated menu open/close — overlay so toggler stays visible */
          .navbar-collapse {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            display: block !important;
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.3s ease;
            background-color: white;
            padding: 0;
            z-index: 1039;
          }
          .navbar-collapse.show {
            max-height: 80vh;
            overflow-y: auto;
            box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          }
          .navbar-nav {
            width: 100%;
            padding: 0.25rem 0;
          }

          /* Parent nav items */
          .navbar-nav .nav-item {
            border-bottom: 1px solid #f4f5f7;
          }
          .navbar-nav .nav-item:last-child {
            border-bottom: none;
          }
          .navbar-nav .nav-link {
            padding: 0.875rem 1rem;
            justify-content: space-between;
            font-weight: 600;
            color: #32325d;
            min-height: 48px;
            display: flex;
            align-items: center;
          }

          /* Animated accordion sub-menus */
          .mega-dropdown {
            position: static;
            box-shadow: none;
            transform: none;
            visibility: visible;
            min-width: 0;
            border-radius: 0;
            background: #fafbfc;
            padding: 0;
            max-height: 0;
            overflow: hidden;
            opacity: 0;
            display: block;
            transition: max-height 0.25s ease, opacity 0.2s ease;
          }
          .nav-mega.mobile-open > .mega-dropdown {
            max-height: 500px;
            opacity: 1;
            border-left: 3px solid var(--primary);
          }
          .mega-dropdown .mega-item {
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 0.625rem 1rem 0.625rem 1.5rem;
            border-bottom: 1px solid #f4f5f7;
            min-height: 48px;
          }
          .mega-dropdown .mega-item:last-child {
            border-bottom: none;
          }
          .mega-dropdown .mega-item:hover,
          .mega-dropdown .mega-item:active {
            background: #f6f9fc;
          }
          .mega-dropdown .mega-item-title {
            font-size: 0.82rem;
            font-weight: 500;
          }
          .mega-dropdown .mega-item-desc {
            font-size: 0.78rem;
            color: #6b7c93;
          }
          .nav-mega > .nav-link::after {
            transition: transform 0.2s;
          }
          .nav-mega.mobile-open > .nav-link::after {
            transform: rotate(180deg);
          }

          /* Right section (lang + login) */
          .navbar-right-section {
            border-top: 1px solid #f4f5f7;
            margin-top: 0.25rem;
            padding-top: 0.25rem;
          }

          /* Language selector as inline row */
          .lang-selector {
            padding: 0 !important;
            border-bottom: none !important;
          }
          .lang-toggle {
            width: 100%;
            justify-content: space-between;
            border: none;
            padding: 0.875rem 1rem;
            font-size: 0.85rem;
            border-radius: 0;
            min-height: 48px;
          }
          .lang-toggle img {
            width: 24px;
            height: 17px;
          }
          .lang-toggle-caret {
            transition: transform 0.2s;
          }
          .lang-open .lang-toggle-caret {
            transform: rotate(180deg);
          }
          /* Language dropdown accordion */
          .lang-dropdown-menu {
            display: block !important;
            position: static;
            box-shadow: none;
            border: none;
            border-radius: 0;
            background: #fafbfc;
            margin: 0;
            padding: 0;
            width: 100%;
            max-height: 0;
            overflow: hidden;
            opacity: 0;
            transition: max-height 0.25s ease, opacity 0.2s ease;
          }
          .lang-open > .lang-dropdown-menu {
            max-height: 300px;
            opacity: 1;
          }
          .lang-dropdown-menu button {
            padding: 0.75rem 1rem 0.75rem 1.5rem;
            min-height: 48px;
          }
          .lang-dropdown-menu button img {
            width: 24px;
            height: 17px;
          }

          /* Login button as bottom CTA */
          .nav-login-item {
            padding: 0 !important;
            border-bottom: none !important;
          }
          .nav-login-item .btn {
            display: block;
            width: calc(100% - 2rem);
            margin: 0.75rem 1rem 0.5rem;
            padding: 0.75rem;
            font-size: 0.95rem;
            font-weight: 600;
            border-radius: 8px;
            text-align: center;
          }
        }
      `}</style>
      <nav className="navbar navbar-main navbar-expand-lg bg-white navbar-light position-sticky top-0 shadow py-2" style={{ zIndex: 1040 }}>
        <div className="container">
          <Link className="navbar-brand" to="/" style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
            <img src="/assets/ic_launcher.png" alt="DysGo! App Icon" style={{ width: 30, height: 30, objectFit: 'contain', marginRight: 10 }} />
            DysGo!™
          </Link>
          <button className="navbar-toggler" type="button" aria-controls="navbar-default" aria-expanded={mobileMenuOpen} aria-label="Toggle navigation" onClick={() => setMobileMenuOpen(prev => !prev)}>
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className={`collapse navbar-collapse${mobileMenuOpen ? ' show' : ''}`} id="navbar-default">
            {/* Left nav: mega-menu sections */}
            <ul className="navbar-nav mr-auto">
              <MegaMenuItem label={t('nav.product_offering')}>
                <a className="mega-item" href="/#download">
                  <p className="mega-item-title">{t('nav.dysgo_app')}</p>
                  <p className="mega-item-desc">{t('nav.dysgo_app_desc')}</p>
                </a>
                <a className="mega-item" href="/app/auth/login">
                  <p className="mega-item-title">{t('nav.dysgo_web')}</p>
                  <p className="mega-item-desc">{t('nav.dysgo_web_desc')}</p>
                </a>
              </MegaMenuItem>

              <MegaMenuItem label={t('nav.resources')}>
                <Link className="mega-item" to="/knowledge">
                  <p className="mega-item-title">{t('nav.knowledge_base')}</p>
                  <p className="mega-item-desc">{t('nav.knowledge_base_desc')}</p>
                </Link>
                <Link className="mega-item" to="/articles">
                  <p className="mega-item-title">{t('nav.articles')}</p>
                  <p className="mega-item-desc">{t('nav.articles_desc')}</p>
                </Link>
              </MegaMenuItem>

              <MegaMenuItem label={t('nav.about')}>
                <a className="mega-item" href="/#about">
                  <p className="mega-item-title">{t('nav.company')}</p>
                  <p className="mega-item-desc">{t('nav.company_desc')}</p>
                </a>
                <a className="mega-item" href="/#events">
                  <p className="mega-item-title">{t('nav.events')}</p>
                  <p className="mega-item-desc">{t('nav.events_desc')}</p>
                </a>
                <a className="mega-item" href="/app/public/support">
                  <p className="mega-item-title">{t('nav.support')}</p>
                  <p className="mega-item-desc">{t('nav.support_desc')}</p>
                </a>
              </MegaMenuItem>
            </ul>

            {/* Right nav: language selector + Login */}
            <ul className="navbar-nav ml-auto align-items-lg-center navbar-right-section">
              <li className={`nav-item lang-selector${langOpen ? ' lang-open' : ''}`} ref={langRef}>
                <button
                  className="lang-toggle"
                  onClick={() => setLangOpen(prev => !prev)}
                  aria-expanded={langOpen}
                  aria-haspopup="true"
                >
                  <img src={currentLang.flag} alt={currentLang.label} />
                  <span>{currentLang.label}</span>
                  <span className="lang-toggle-caret"></span>
                </button>
                <div className="lang-dropdown-menu">
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      className={lang.code === currentLang.code ? 'active' : ''}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setLangOpen(false);
                      }}
                    >
                      <img src={lang.flag} alt={lang.label} />
                      {lang.label}
                    </button>
                  ))}
                </div>
              </li>
              <li className="nav-item nav-login-item">
                <a className="btn btn-primary btn-sm" href="/app/auth/login">{t('auth_login')}</a>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </>
  );
};

/* ---- Mega-menu item (hover on desktop, click on mobile) ---- */
const MegaMenuItem: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <li
      className={`nav-item nav-mega${mobileOpen ? ' mobile-open' : ''}`}
      onMouseEnter={() => {
        // Desktop: handled by CSS :hover
      }}
    >
      <span
        className="nav-link"
        role="button"
        tabIndex={0}
        onClick={() => setMobileOpen(prev => !prev)}
        onKeyDown={e => { if (e.key === 'Enter') setMobileOpen(prev => !prev); }}
      >
        {label}
      </span>
      <div className="mega-dropdown">
        {children}
      </div>
    </li>
  );
};

export default PublicNavbar;
